import React from "react";
import { getBaseUrl } from "../../api/api";
import DefaultAvatar from "../../components/DefaultAvatar";
import DealsSVG from "../../icons/svg/deals.svg?react";
import CompanySVG from "../../icons/svg/companies.svg?react";
import PeopleSVG from "../../icons/svg/people.svg?react";

export { DealsSVG, CompanySVG, PeopleSVG };

export type LinkItemOption = {
  value: string;
  label: string;
  name?: string;
  description?: string;
  iconUrl?: string;
  type: string;
  id: string;
  projectId?: string;
};

export interface BasePickerProps {
  onChange?: (item: LinkItemOption | null) => void;
  items?: any; // Immutable or plain object
  projectId: string;
  placeholder?: string;
  autoOpen?: boolean;
  className?: string;
  noBorder?: boolean;
  showBorder?: boolean;
  maxWidth?: string;
  width?: string;
  popoverMinWidth?: string;
  showInfoIcon?: boolean;
  showSelectIcon?: boolean;
  hideSelectIconUntilHover?: boolean;
  showIcon?: boolean;
  isViewMode?: boolean;
  projectView?: string;
  customTrigger?: React.ReactNode;
  showTriggerHeight?: boolean;
  useInApp?: boolean;
}

/**
 * Record badge wrapper for SVG icons.
 * Square badge for objects (Deal, Company), circle for people (Contact).
 */
export const RecordBadge = ({
  Icon,
  shape = "square",
  className = "w-4 h-4",
  iconScale = 1.3,
}: {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  shape?: "square" | "circle";
  className?: string;
  iconScale?: number;
}) => (
  <div
    className={`flex items-center justify-center shrink-0 bg-gray-200 overflow-hidden ${
      shape === "circle" ? "rounded-full" : "rounded"
    } ${className}`}
  >
    <Icon
      width={14}
      height={14}
      style={{
        color: "#6B7280",
        display: "block",
        transform: `scale(${iconScale})`,
        transformOrigin: "center",
      }}
    />
  </div>
);

const getItemData = (item: any, type: string) => {
  if (item == null) {
    return { dataType: type, iconUrl: "" };
  } else {
    const dataType = item.type || type;
    return { dataType, iconUrl: item.iconUrl };
  }
};

export const renderItemIcon = (item: any = {}, type = "", w = "w-4 h-4") => {
  if (!item || item === null) {
    return null;
  }
  const appUrl = getBaseUrl();
  const { dataType, iconUrl } = getItemData(item, type);

  switch (dataType) {
    case "TASK":
    case "ISSUE":
      if (iconUrl && iconUrl != "") {
        return <img src={item.iconUrl} className={w} alt={""} />;
      }
      return <img src={`${appUrl}/static/images/jsm.svg`} className={w} alt={""} />;
    case "SALE":
      return <RecordBadge Icon={DealsSVG} shape="square" className={w} />;
    case "CONTACT":
      if (iconUrl && iconUrl != "") {
        return <img src={item.iconUrl} className={`${w} rounded-full object-cover`} alt={""} />;
      }
      return (
        <DefaultAvatar
          name={(item && (item.label || item.name)) || ""}
          type="CONTACT"
          size="sm"
          className={`${w} rounded-full bg-blue-100 text-blue-800 text-xs font-semibold flex items-center justify-center`}
        />
      );
    case "COMPANY":
      if (iconUrl && iconUrl != "") {
        return <img src={item.iconUrl} className={`${w} rounded-lg object-cover`} alt={""} />;
      }
      return <RecordBadge Icon={CompanySVG} shape="square" className={w} />;
    default:
      return null;
  }
};

export const renderAvatar = (item: LinkItemOption, type: string, w = "w-4 h-4") => {
  if (!item || (type !== "CONTACT" && type !== "COMPANY")) return null;
  const { iconUrl } = getItemData(item, type);
  if (iconUrl && iconUrl !== "") {
    return <img src={iconUrl} className={`${w} rounded-lg object-cover shrink-0`} alt="" />;
  }
  if (type === "CONTACT") {
    return (
      <DefaultAvatar
        name={item.label || item.name || ""}
        type="CONTACT"
        size="sm"
        className={`${w} rounded-full bg-blue-100 text-blue-800 text-xs font-semibold flex items-center justify-center shrink-0`}
      />
    );
  }
  if (type === "COMPANY") {
    return <RecordBadge Icon={CompanySVG} shape="square" className={w} />;
  }
};
